import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom"; // Added Link for the back button
import styled from "styled-components";
import { api, endpoints } from "../../utils/axiosInstance";

const MenuDetailPage = () => {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const StarRating = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const totalStars = 5;

    return (
      <StarContainer>
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`}>⭐</span>
        ))}
        {hasHalfStar && <span>⭐️</span>}
        {[...Array(totalStars - fullStars - (hasHalfStar ? 1 : 0))].map(
          (_, i) => (
            <span key={`empty-${i}`}>☆</span>
          )
        )}
        <RatingText>({rating})</RatingText>
      </StarContainer>
    );
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await api.get(endpoints.menus.bySlug(slug));
        if (!response) {
          throw new Error("Không tìm thấy thực đơn");
        }
        setMenu(response);
      } catch (err) {
        setError(
          err.response?.message ||
            "Không thể tải chi tiết thực đơn. Vui lòng thử lại sau."
        );
        console.error("Lỗi khi lấy dữ liệu menu:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [slug]);

  if (loading) return <LoadingMessage>Đang tải...</LoadingMessage>;
  if (error) return <ErrorMessage>{error}</ErrorMessage>;
  if (!menu) return <ErrorMessage>Không tìm thấy thực đơn.</ErrorMessage>;

  return (
    <Container>
      {/* Image Header */}
      <ImageHeader>
        <img
          src={menu?.imageUrl || "/images/menu-placeholder.jpg"}
          alt={menu?.name}
        />
      </ImageHeader>

      {/* Menu Title and Description */}
      <Title>{menu?.name}</Title>
      {menu?.description ? (
        <Description>{menu.description}</Description>
      ) : (
        <Description>Chưa có mô tả cho thực đơn này.</Description>
      )}

      {/* Menu Info */}
      <Info>
        <InfoItem>🍽️ Phục vụ: {menu?.serves || 1} người</InfoItem>
        <InfoItem>🕒 Loại bữa ăn: {menu?.type || "Không xác định"}</InfoItem>
        <InfoItem>
          🏷️ Tags:{" "}
          {menu?.tags && menu.tags.length > 0
            ? menu.tags.map((tag, index) => <Tag key={index}>{tag}</Tag>)
            : "Không có tag"}
        </InfoItem>
        <InfoItem>👨‍🍳 Người tạo: {menu?.createdBy?.name || "Ẩn danh"}</InfoItem>
        <InfoItem>🔥 Tổng calories: {menu?.totalCalories || 0} kcal</InfoItem>
      </Info>

      <SectionTitle>📝 Danh sách món ăn</SectionTitle>

      {/* Recipe List */}
      {menu?.recipes && menu.recipes.length > 0 ? (
        <div>
          {menu.recipes.map(({ recipe, servings }, index) =>
            recipe ? (
              <RecipeCard key={recipe._id || index}>
                <CardContent>
                  <RecipeImage
                    src={recipe.imageUrl || "/images/recipe-placeholder.jpg"}
                    alt={recipe.name}
                  />
                  <RecipeDetails>
                    <RecipeTitle>{recipe.name}</RecipeTitle>
                    <StarRating rating={recipe.rating || 0} />
                    <RecipeMetrics>
                      <MetricItem>🍽️ {servings || 1} khẩu phần</MetricItem>
                    </RecipeMetrics>
                    {recipe.ingredients && recipe.ingredients.length > 0 && (
                      <RecipeInfo>
                        🥗 {recipe.ingredients.length} nguyên liệu
                      </RecipeInfo>
                    )}
                  </RecipeDetails>
                  <ViewButtonWrapper>
                    <Link to={`/recipes/${recipe.slug}`}>
                      <ViewButton>Xem chi tiết</ViewButton>
                    </Link>
                    <ViewsText>👁️ {recipe.views || 0} lượt xem</ViewsText>
                  </ViewButtonWrapper>
                </CardContent>
              </RecipeCard>
            ) : (
              <ErrorMessage key={index}>
                ⚠️ Công thức này đã bị xóa hoặc không tồn tại.
              </ErrorMessage>
            )
          )}
        </div>
      ) : (
        <ErrorMessage>Thực đơn này chưa có món ăn nào.</ErrorMessage>
      )}

      {/* Back Button */}
      <BackButtonWrapper>
        <Link to="/menus">
          <BackButton>Quay lại danh sách thực đơn</BackButton>
        </Link>
      </BackButtonWrapper>
    </Container>
  );
};

export default MenuDetailPage;

// ===== Styled Components =====
const Container = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: auto;
`;

const ImageHeader = styled.div`
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-radius: 12px;
  margin-bottom: 1.5rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
`;

const Info = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: #666;
  margin-bottom: 2rem;
  justify-content: center;
`;

const InfoItem = styled.span`
  font-size: 1rem;
  background: #f0f0f0;
  padding: 0.5rem 1rem;
  border-radius: 20px;
`;

const Tag = styled.span`
  display: inline-block;
  background: rgb(255, 159, 28); // Thay đổi màu tag
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  margin-right: 0.5rem;
  font-size: 0.9rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const RecipeCard = styled.div`
  background: #ffffff;
  border: 1px solid #eee;
  padding: 1.5rem;
  margin: 1rem 0;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RecipeTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  margin-bottom: 0.5rem;
`;

const RecipeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
  font-size: 0.95rem;

  svg {
    color: #666;
  }
`;

const RecipeMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: #f8f8f8;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const LoadingMessage = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  padding: 2rem;
`;

const ErrorMessage = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: rgb(255, 159, 28); // Thay đổi màu lỗi
  padding: 2rem;
`;

const BackButtonWrapper = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const BackButton = styled.button`
  background-color: rgb(255, 159, 28); // Thay đổi màu nút
  color: white;
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgb(255, 174, 66); // Thay đổi màu hover
  }
`;

const CardContent = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RecipeImage = styled.img`
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  background-color: #f5f5f5;
`;

const RecipeDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ViewButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const ViewButton = styled.button`
  background: rgb(255, 159, 28); // Thay đổi màu nút
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgb(255, 174, 66); // Thay đổi màu hover
    transform: translateY(-2px);
  }
`;

const ViewsText = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const StarContainer = styled.div`
  font-size: 1.2rem;
  color: rgb(255, 159, 28); // Thay đổi màu sao
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RatingText = styled.span`
  color: #555;
  font-size: 0.9rem;
`;
